/**
 * Admin Management Page
 * 
 * Displays admin management page for Location Assignments.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, MapPin, AlertCircle, Shield, Users, Building2 } from "lucide-react";
import { AdminLocationAssignmentTable } from "./components/AdminLocationAssignmentTable";
import { AdminLocationAssignmentDialog } from "./components/AdminLocationAssignmentDialog";
import { AdminLevelTable } from "./components/AdminLevelTable";
import { AdminLevelDialog } from "./components/AdminLevelDialog";
import { DeleteAdminLevelDialog } from "./components/DeleteAdminLevelDialog";
import { useAdminLocationAssignments, useCreateLocationAssignment, useUpdateLocationAssignment, useDeleteLocationAssignment } from "./hooks/useAdminLocationAssignments";
import { useAdminLevels, useDeleteAdminLevel } from "./hooks/useAdminLevels";
import { toast } from "sonner";
import type { AdminLocationAssignment, AdminLevel, AdminLevelItem, AdminLevelType } from "@/services/adminService";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { getNumericUserLevel, hasAnyPrivilege } from "@/utils/privileges";

export function AdminManagementPage() {
  const { user, refreshUser } = useAuth();
  const numericLevel = getNumericUserLevel(user);
  const isLevel3 = numericLevel === 3;
  const canManageAdminLevels = isLevel3;
  const canManageAssignments = isLevel3 || hasAnyPrivilege(user, ["manage_admin_assignments"]);

  // Determine user's max admin level from assignments
  const maxLevel = React.useMemo(() => {
    if (isLevel3) return "nasional" as const;
    const levels = user?.adminAssignments?.filter((a) => a.isActive).map((a) => a.level) ?? [];
    if (levels.includes("nasional")) return "nasional" as const;
    if (levels.includes("province")) return "province" as const;
    if (levels.includes("city")) return "city" as const;
    return null;
  }, [user, isLevel3]);

  const [activeTab, setActiveTab] = React.useState<"admin-levels" | "location-assignments">(
    canManageAdminLevels ? "admin-levels" : "location-assignments"
  );

  React.useEffect(() => {
    // If user can't see admin-levels tab, keep them on location assignments.
    if (!canManageAdminLevels && activeTab === "admin-levels") {
      setActiveTab("location-assignments");
    }
  }, [canManageAdminLevels, activeTab]);

  // Admin level states
  const [adminLevelFilter, setAdminLevelFilter] = React.useState<AdminLevelType | undefined>(undefined);
  const { adminLevels, loading: adminLevelsLoading, error: adminLevelsError, fetchAdminLevels } = useAdminLevels(adminLevelFilter);
  const { deleteAdminLevel } = useDeleteAdminLevel();

  // Admin level dialog states
  const [adminLevelDialogOpen, setAdminLevelDialogOpen] = React.useState(false);
  const [selectedAdminLevel, setSelectedAdminLevel] = React.useState<AdminLevelItem | null>(null);
  const [deleteAdminLevelDialogOpen, setDeleteAdminLevelDialogOpen] = React.useState(false);
  const [adminLevelToDelete, setAdminLevelToDelete] = React.useState<AdminLevelItem | null>(null);

  // Location assignment states
  const [locationFilters, setLocationFilters] = React.useState<{
    level?: AdminLevel;
    isActive?: boolean;
    province?: string;
  }>({});
  const { assignments: locationAssignments, loading: locationLoading, error: locationError, fetchAssignments: fetchLocationAssignments } = useAdminLocationAssignments(locationFilters);
  const { createLocationAssignment } = useCreateLocationAssignment();
  const { updateLocationAssignment } = useUpdateLocationAssignment();
  const { deleteLocationAssignment } = useDeleteLocationAssignment();

  // Location assignment dialog states
  const [locationDialogOpen, setLocationDialogOpen] = React.useState(false);
  const [selectedLocationAssignment, setSelectedLocationAssignment] = React.useState<AdminLocationAssignment | null>(null);
  const [deleteLocationDialogOpen, setDeleteLocationDialogOpen] = React.useState(false);
  const [locationToDelete, setLocationToDelete] = React.useState<AdminLocationAssignment | null>(null);

  // Calculate admin level statistics
  const adminLevelStats = React.useMemo(() => {
    const total = adminLevels.length;
    const nasional = adminLevels.filter((al) => al.level === "nasional").length;
    const provinsi = adminLevels.filter((al) => al.level === "province").length;
    const kota = adminLevels.filter((al) => al.level === "city").length;
    return { total, nasional, provinsi, kota };
  }, [adminLevels]);

  // Admin level handlers
  const handleCreateAdminLevel = () => {
    setSelectedAdminLevel(null);
    setAdminLevelDialogOpen(true);
  };

  const handleEditAdminLevel = (adminLevel: AdminLevelItem) => {
    setSelectedAdminLevel(adminLevel);
    setAdminLevelDialogOpen(true);
  };

  const handleDeleteAdminLevel = (adminLevel: AdminLevelItem) => {
    setAdminLevelToDelete(adminLevel);
    setDeleteAdminLevelDialogOpen(true);
  };

  const handleDeleteAdminLevelConfirm = async () => {
    if (!adminLevelToDelete) return;

    try {
      await deleteAdminLevel(adminLevelToDelete._id);
      fetchAdminLevels();
      setAdminLevelToDelete(null);
      setDeleteAdminLevelDialogOpen(false);
    } catch (error) {
      console.error("Error deleting admin level:", error);
    }
  };

  const handleAdminLevelSuccess = () => {
    fetchAdminLevels();
  };

  // Handle create location assignment
  const handleCreateLocation = () => {
    setSelectedLocationAssignment(null);
    setLocationDialogOpen(true);
  };

  // Handle edit location assignment
  const handleEditLocation = (assignment: AdminLocationAssignment) => {
    setSelectedLocationAssignment(assignment);
    setLocationDialogOpen(true);
  };

  // Handle delete location assignment
  const handleDeleteLocation = (assignment: AdminLocationAssignment) => {
    setLocationToDelete(assignment);
    setDeleteLocationDialogOpen(true);
  };

  // Handle delete location confirm
  const handleDeleteLocationConfirm = async () => {
    if (!locationToDelete) return;

    try {
      await deleteLocationAssignment(locationToDelete._id);
      toast.success("Penugasan lokasi berhasil dihapus", {
        description: `Penugasan lokasi untuk ${locationToDelete.admin?.nama_lengkap ?? "-"} telah dihapus`,
      });
      const affectedId = locationToDelete.admin?._id;
      if (user?.id && affectedId && user.id === affectedId) {
        await refreshUser();
      }
      fetchLocationAssignments();
      setLocationToDelete(null);
      setDeleteLocationDialogOpen(false);
    } catch (error) {
      console.error("Error deleting location assignment:", error);
      toast.error("Gagal menghapus penugasan lokasi", {
        description: error instanceof Error ? error.message : "Terjadi kesalahan saat menghapus penugasan lokasi",
      });
    }
  };

  // Handle location assignment success
  const handleLocationAssignmentSuccess = () => {
    fetchLocationAssignments();
  };

  const provinceTabs = React.useMemo(() => {
    // Only show province tabs when browsing ORLOK (city) assignments
    if (locationFilters.level !== "city") return [];
    const set = new Set<string>();
    for (const a of locationAssignments) {
      for (const p of a.provinces || []) {
        const t = String(p || "").trim();
        if (t) set.add(t);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [locationAssignments, locationFilters.level]);

  return (
    <div className="flex-1 space-y-6 p-6 overflow-x-hidden">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Manajemen Admin</h1>
          <p className="text-muted-foreground mt-1">
            Kelola admin level dan penugasan admin
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "admin-levels" | "location-assignments")} className="space-y-6">
        <TabsList>
          {canManageAdminLevels && (
            <TabsTrigger value="admin-levels">
              <Shield className="h-4 w-4 mr-2" />
              Admin Levels
            </TabsTrigger>
          )}
          {canManageAssignments && (
            <TabsTrigger value="location-assignments">
              <MapPin className="h-4 w-4 mr-2" />
              Penugasan Lokasi
            </TabsTrigger>
          )}
        </TabsList>

        {/* Admin Levels Tab */}
        {canManageAdminLevels && (
          <TabsContent value="admin-levels" className="space-y-6">
          {/* Statistics Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Admin Level</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{adminLevelStats.total}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Nasional</CardTitle>
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{adminLevelStats.nasional}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Provinsi</CardTitle>
                <Building2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{adminLevelStats.provinsi}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Kota/Kabupaten</CardTitle>
                <MapPin className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{adminLevelStats.kota}</div>
              </CardContent>
            </Card>
          </div>

          {/* Admin Levels Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Admin Levels</CardTitle>
                  <CardDescription>
                    Kelola template admin level untuk penugasan admin
                  </CardDescription>
                </div>
                <Button onClick={handleCreateAdminLevel} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Tambah Admin Level
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <Label htmlFor="admin-level-filter">Level</Label>
                  <Select
                    value={adminLevelFilter || "all"}
                    onValueChange={(value) => {
                      setAdminLevelFilter(value === "all" ? undefined : value as AdminLevelType);
                    }}
                  >
                    <SelectTrigger id="admin-level-filter" className="h-10">
                      <SelectValue placeholder="Semua Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Level</SelectItem>
                      <SelectItem value="nasional">Nasional</SelectItem>
                      <SelectItem value="province">Provinsi</SelectItem>
                      <SelectItem value="city">Kota/Kabupaten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {adminLevelsError && (
                <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {adminLevelsError}
                    </p>
                  </div>
                </div>
              )}
              <AdminLevelTable
                adminLevels={adminLevels}
                loading={adminLevelsLoading}
                onEdit={handleEditAdminLevel}
                onDelete={handleDeleteAdminLevel}
              />
            </CardContent>
          </Card>
          </TabsContent>
        )}

        {/* Location Assignments Tab */}
        {canManageAssignments && (
          <TabsContent value="location-assignments" className="space-y-6">
          {/* Location Assignments */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Penugasan Lokasi Admin</CardTitle>
                  <CardDescription>
                    Kelola lokasi yang dapat dikelola oleh admin
                  </CardDescription>
                </div>
                <Button onClick={handleCreateLocation} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Tambah Penugasan Lokasi
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <Label htmlFor="level-filter">Level</Label>
                  <Select
                    value={locationFilters.level || "all"}
                    onValueChange={(value) => {
                      setLocationFilters({
                        ...locationFilters,
                        level: value === "all" ? undefined : value as AdminLevel,
                        // reset province tab when level changes
                        province: undefined,
                      });
                    }}
                  >
                    <SelectTrigger id="level-filter" className="h-10">
                      <SelectValue placeholder="Semua Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Level</SelectItem>
                      {maxLevel === "nasional" && <SelectItem value="nasional">Nasional</SelectItem>}
                      {(maxLevel === "nasional" || maxLevel === "province") && <SelectItem value="province">Provinsi</SelectItem>}
                      <SelectItem value="city">Kota/Kabupaten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label htmlFor="status-filter">Status</Label>
                  <Select
                    value={locationFilters.isActive === undefined ? "all" : locationFilters.isActive ? "active" : "inactive"}
                    onValueChange={(value) => {
                      setLocationFilters({
                        ...locationFilters,
                        isActive: value === "all" ? undefined : value === "active",
                      });
                    }}
                  >
                    <SelectTrigger id="status-filter" className="h-10">
                      <SelectValue placeholder="Semua Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Status</SelectItem>
                      <SelectItem value="active">Aktif</SelectItem>
                      <SelectItem value="inactive">Tidak Aktif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Province tabs for ORLOK (city) */}
              {locationFilters.level === "city" && provinceTabs.length > 0 && (
                <div className="mb-4">
                  <Tabs
                    value={locationFilters.province || "all"}
                    onValueChange={(v) => {
                      setLocationFilters((prev) => ({
                        ...prev,
                        province: v === "all" ? undefined : v,
                      }));
                    }}
                  >
                    <TabsList className="flex flex-wrap h-auto">
                      <TabsTrigger value="all">Semua Provinsi</TabsTrigger>
                      {provinceTabs.map((p) => (
                        <TabsTrigger key={p} value={p}>
                          {p}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </div>
              )}

              {locationError && (
                <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {locationError}
                    </p>
                  </div>
                </div>
              )}
              <AdminLocationAssignmentTable
                assignments={locationAssignments}
                loading={locationLoading}
                onEdit={handleEditLocation}
                onDelete={handleDeleteLocation}
              />
            </CardContent>
          </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialogs */}
      {/* Admin Level Dialogs */}
      <AdminLevelDialog
        open={adminLevelDialogOpen}
        onOpenChange={(open) => {
          setAdminLevelDialogOpen(open);
          if (!open) {
            setSelectedAdminLevel(null);
          }
        }}
        adminLevel={selectedAdminLevel}
        onSuccess={handleAdminLevelSuccess}
      />

      <DeleteAdminLevelDialog
        open={deleteAdminLevelDialogOpen}
        onOpenChange={setDeleteAdminLevelDialogOpen}
        adminLevel={adminLevelToDelete}
        onConfirm={handleDeleteAdminLevelConfirm}
      />

      {/* Location Assignment Dialogs */}
      <AdminLocationAssignmentDialog
        open={locationDialogOpen}
        onOpenChange={(open) => {
          setLocationDialogOpen(open);
          if (!open) {
            setSelectedLocationAssignment(null);
          }
        }}
        assignment={selectedLocationAssignment}
        onSuccess={handleLocationAssignmentSuccess}
      />

      {/* Delete Location Assignment Dialog */}
      <AlertDialog open={deleteLocationDialogOpen} onOpenChange={setDeleteLocationDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Penugasan Lokasi?</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus penugasan lokasi untuk{" "}
              <strong>{locationToDelete?.admin?.nama_lengkap ?? "-"}</strong>? Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLocationConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

